import React, { useEffect } from 'react';

import { Amuleto } from './sections/Amuleto';
import { Depoimentos } from './sections/Depoimentos';
import { Faq } from './sections/Faq';
import { Fechamento } from './sections/Fechamento';
import { Investimento } from './sections/Investimento';
import { ComoFunciona } from './sections/ComoFunciona';
import { ManuelaClinica } from './sections/ManuelaClinica';
import { Percurso } from './sections/Percurso';
import { QuemConduz } from './sections/QuemConduz';
import { SeloTerapeutico } from './sections/SeloTerapeutico';
import { Hero } from './Hero';
import { PageAnimations } from './PageAnimations';
import { SiteHeader } from './SiteHeader';
import { ButtonLink } from '../../components/ButtonLink/ButtonLink';
import { datas, faqs, planos, site } from '../../utils/copy';
import { useProductFavicon } from 'hooks/useFavicon';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import 'styles/conversas-terapeuticas.css';

function JsonLd() {
  const data = [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: `${site.name} ${site.assinatura}`,
      description: site.description,
      inLanguage: 'pt-BR',
      provider: { '@type': 'Person', name: site.author },
      offers: [planos.trimestral, planos.anual].map((plano) => ({
        '@type': 'Offer',
        name: plano.nome,
        price: plano.precoTotal,
        priceCurrency: 'BRL',
        validFrom: datas.aberturaISO,
        validThrough: datas.fechamentoISO,
      })),
    },
  ];

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data).replace(/</g, '\\u003c') }} />;
}

/* Espelha o script inline do <head> do projeto original (app/layout.tsx):
   --hero-vvh = menor altura visível (Safari chrome). Aqui roda em useEffect
   em vez de beforeInteractive porque a página vive numa SPA React comum, não
   no App Router do Next. */
function useHeroVvhSync() {
  useEffect(() => {
    function sync() {
      const list = [window.innerHeight];
      if (window.visualViewport?.height) {
        list.push(window.visualViewport.height);
      }
      if (document.documentElement?.clientHeight) {
        list.push(document.documentElement.clientHeight);
      }
      const h = Math.min(...list);
      if (!(h > 0)) return;
      document.documentElement.style.setProperty('--hero-vvh', `${Math.round(h)}px`);
    }

    sync();
    window.visualViewport?.addEventListener('resize', sync);
    window.visualViewport?.addEventListener('scroll', sync);
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    const t1 = setTimeout(sync, 50);
    const t2 = setTimeout(sync, 300);

    return () => {
      window.visualViewport?.removeEventListener('resize', sync);
      window.visualViewport?.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
      clearTimeout(t1);
      clearTimeout(t2);
      document.documentElement.style.removeProperty('--hero-vvh');
    };
  }, []);
}

/* html.ct-smooth-scroll liga o scroll suave só enquanto esta página está
   montada (a regra correspondente em conversas-terapeuticas.css depende
   dela); sem isso `scroll-behavior: smooth` teria que ir direto em <html>,
   afetando a navegação por âncora do resto da plataforma. */
function useSmoothScrollScope() {
  useEffect(() => {
    document.documentElement.classList.add('ct-smooth-scroll');
    return () => {
      document.documentElement.classList.remove('ct-smooth-scroll');
    };
  }, []);
}

export default function ConversasTerapeuticasLandingPage() {
  useHeroVvhSync();
  useSmoothScrollScope();
  useProductFavicon(true);
  useDocumentTitle('Conversas Terapêuticas');

  return (
    <div className="ct-page">
      <JsonLd />
      <a href="#conteudo" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[70] focus:flex focus:min-h-11 focus:items-center focus:bg-cta focus:px-4 focus:py-2">
        Pular pro conteúdo
      </a>
      <SiteHeader />
      <div aria-hidden="true" className="grain-page" />

      <PageAnimations>
        <main id="conteudo" tabIndex={-1} className="outline-none">
          <div className="poster-bleed poster-bleed-hero"><Hero /></div>
          <div className="poster-bleed poster-bleed-clinic"><ManuelaClinica /></div>
          <div className="poster-bleed poster-bleed-therapeutic-seal"><SeloTerapeutico /></div>
          <div className="poster-bleed poster-bleed-conductor"><QuemConduz /></div>
          <Percurso />
          <div className="poster-bleed poster-bleed-how"><ComoFunciona /></div>
          {/* depoimentos sobem antes da compra (doc jul/2026) */}
          <div className="poster-bleed poster-bleed-testimonials"><Depoimentos /></div>
          <div className="poster-bleed poster-bleed-investment"><Investimento /></div>
          {/* logo depois do preço: o card do anual promete o colar, esta seção
              conta o que ele é */}
          <div className="poster-bleed poster-bleed-amulet"><Amuleto /></div>
          <div className="poster-bleed poster-bleed-faq"><Faq /></div>
          <div className="poster-bleed poster-bleed-closing"><Fechamento /></div>
        </main>
      </PageAnimations>

      {/* CTA persistente. Deixou de ser só mobile: no desktop o header é
          absolute e sai de tela ao rolar, então fora do hero, do investimento e
          do fechamento não sobrava nenhuma ação na tela. No gate wide o CSS
          transforma esta barra numa ilha compacta centrada na base,
          reaproveitando as zonas mudas e o foco que já estavam calibrados. */}
      <div id="cta-mobile" className="fixed inset-x-0 bottom-0 z-40 translate-y-full bg-bege/95 px-4 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))] backdrop-blur transition-transform duration-300 ease-out data-[visible]:translate-y-0">
        <ButtonLink variant="solid" href="#investimento" className="w-full shadow-[4px_4px_0_var(--color-tinta)]">{planos.anual.cta}</ButtonLink>
      </div>
    </div>
  );
}
